import React, { useState, useEffect } from 'react';
import { recordView } from '../../components/layout/MultiApplyUpsell';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { jobsAPI, userAPI } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { trackSearch, trackJobView } from '../../lib/metaPixel';
import { 
    Search, 
    MapPin, 
    Briefcase, 
    Clock, 
    Building2, 
    PoundSterling,
    Calendar,
    ExternalLink,
    Mail,
    Loader2,
    Filter,
    Lock,
    Crown,
    X,
    Sparkles,
    CheckCircle2,
    Zap,
    TrendingUp,
    Users,
    Star,
    ArrowRight,
    Globe
} from 'lucide-react';

const JobsPage = () => {
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [locationFilter, setLocationFilter] = useState('');
    const [countryFilter, setCountryFilter] = useState('');  // New: Country filter
    const [jobTypeFilter, setJobTypeFilter] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [hasAccess, setHasAccess] = useState(false);
    const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchJobs();
    }, [page, jobTypeFilter, searchTerm, locationFilter, countryFilter]);

    const fetchJobs = async () => {
        setLoading(true);
        try {
            const params = { page, limit: 12 };
            if (searchTerm) {
                params.search = searchTerm;
            }
            if (jobTypeFilter && jobTypeFilter !== 'all') {
                params.job_type = jobTypeFilter;
            }
            if (locationFilter) {
                params.location = locationFilter;
            }
            if (countryFilter) {
                params.country = countryFilter;  // Add country filter
            }
            const response = await jobsAPI.list(params);
            setJobs(response.data.jobs);
            setTotalPages(response.data.pages);
            setHasAccess(response.data.has_access || false);
            
            // Track search in Meta Pixel if there's a search term
            if (searchTerm || locationFilter) {
                trackSearch(searchTerm || locationFilter, 'jobs', response.data.jobs?.length || 0);
            }
        } catch (error) {
            console.error('Failed to fetch jobs:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = () => {
        setPage(1);
        // fetchJobs will be called by useEffect
    };

    // No more client-side filtering - backend handles search
    const displayedJobs = jobs;

    const formatSalary = (job) => {
        if (!job.salary_min && !job.salary_max) return 'Competitive';
        const min = job.salary_min ? `£${job.salary_min.toLocaleString()}` : '';
        const max = job.salary_max ? `£${job.salary_max.toLocaleString()}` : '';
        const period = job.salary_period === 'yearly' ? '/year' : job.salary_period === 'monthly' ? '/month' : '/hour';
        if (min && max) return `${min} - ${max}${period}`;
        if (min) return `From ${min}${period}`;
        if (max) return `Up to ${max}${period}`;
        return 'Competitive';
    };

    const getJobTypeBadge = (type) => {
        const styles = {
            'full-time': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
            'part-time': 'bg-sky-500/20 text-sky-300 border-sky-500/30',
            'contract': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
        };
        return styles[type] || 'bg-white/10 text-white/70 border-white/20';
    };

    const getJobTypeBadgeLight = (type) => {
        const styles = {
            'full-time': 'bg-emerald-100 text-emerald-700 border-emerald-200',
            'part-time': 'bg-sky-100 text-sky-700 border-sky-200',
            'contract': 'bg-amber-100 text-amber-700 border-amber-200',
        };
        return styles[type] || 'bg-neutral-100 text-neutral-600 border-neutral-200';
    };

    const isDeadlinePassed = (deadline) => {
        if (!deadline) return false;
        return new Date(deadline) < new Date();
    };

    // Format time ago
    const formatTimeAgo = (dateString) => {
        if (!dateString) return null;
        
        const date = new Date(dateString);
        const now = new Date();
        const diffInMs = now - date;
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        
        if (diffInHours < 1) {
            return 'Just now';
        } else if (diffInHours < 24) {
            return diffInHours === 1 ? 'An hour ago' : `${diffInHours} hours ago`;
        } else if (diffInDays === 1) {
            return 'A day ago';
        } else if (diffInDays < 7) {
            return `${diffInDays} days ago`;
        } else if (diffInDays < 30) {
            const weeks = Math.floor(diffInDays / 7);
            return weeks === 1 ? 'A week ago' : `${weeks} weeks ago`;
        } else {
            const months = Math.floor(diffInDays / 30);
            return months === 1 ? 'A month ago' : `${months} months ago`;
        }
    };

    // Handle apply click for non-subscribed users
    const handleApplyClick = (job) => {
        setSelectedJob(job);
        setShowSubscriptionModal(true);
        // Track job view for dashboard
        if (user) {
            userAPI.trackView('job', job.job_id).catch(() => {});
        }
    };

    return (
        <Layout>
            <div className="min-h-screen bg-primary-950">
                {/* Hero Section - Deep Purple with Gradient Mesh */}
                <div className="relative overflow-hidden">
                    {/* Background Effects */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary-900 via-primary-950 to-violet-950" />
                    <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-600/20 rounded-full blur-3xl" />
                    <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-violet-600/20 rounded-full blur-3xl" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl" />
                    
                    {/* Grid Pattern Overlay */}
                    <div className="absolute inset-0 opacity-5" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
                    }} />
                    
                    <div className="relative z-10 py-16 lg:py-24">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            {/* Badge */}
                            <div className="flex justify-center mb-6">
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10">
                                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                                    <span className="text-sm text-white/90">Updated daily with fresh opportunities</span>
                                </div>
                            </div>
                            
                            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-4 text-white">
                                Find Your Next <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-300 via-violet-300 to-fuchsia-300">Dream Job</span>
                            </h1>
                            <p className="text-lg md:text-xl text-primary-200 mb-10 max-w-2xl mx-auto text-center">
                                Browse the latest openings from 4,500+ trusted recruitment agencies across UK & Canada
                            </p>

                            {/* Search Bar - Glass Effect */}
                            <div className="max-w-4xl mx-auto">
                                <div className="backdrop-blur-xl bg-white/10 rounded-2xl p-2 border border-white/10 shadow-2xl shadow-primary-900/50">
                                    <div className="flex flex-col md:flex-row gap-2">
                                        <div className="flex-1 relative">
                                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-300" />
                                            <Input
                                                placeholder="Job title, keywords..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                className="pl-12 h-14 bg-white/5 border-white/10 text-white placeholder:text-primary-300/60 focus:bg-white/10 focus:border-primary-400/50 rounded-xl"
                                                data-testid="search-input"
                                            />
                                        </div>
                                        <div className="flex-1 relative">
                                            <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-300" />
                                            <Select value={countryFilter || 'all'} onValueChange={(v) => setCountryFilter(v === 'all' ? '' : v)}>
                                                <SelectTrigger className="h-14 pl-12 bg-white/5 border-white/10 text-white rounded-xl">
                                                    <SelectValue placeholder="Country" />
                                                </SelectTrigger>
                                                <SelectContent className="bg-primary-900 border-primary-700">
                                                    <SelectItem value="all" className="text-white hover:bg-primary-800">All Countries</SelectItem>
                                                    <SelectItem value="UK" className="text-white hover:bg-primary-800">🇬🇧 UK</SelectItem>
                                                    <SelectItem value="Canada" className="text-white hover:bg-primary-800">🇨🇦 Canada</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="flex-1 relative">
                                            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-primary-300" />
                                            <Input
                                                placeholder="City or postcode"
                                                value={locationFilter}
                                                onChange={(e) => setLocationFilter(e.target.value)}
                                                className="pl-12 h-14 bg-white/5 border-white/10 text-white placeholder:text-primary-300/60 focus:bg-white/10 focus:border-primary-400/50 rounded-xl"
                                                data-testid="location-input"
                                            />
                                        </div>
                                        <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
                                            <SelectTrigger 
                                                className="w-full md:w-44 h-14 bg-white/5 border-white/10 text-white rounded-xl focus:bg-white/10 focus:border-primary-400/50"
                                                data-testid="job-type-filter"
                                            >
                                                <SelectValue placeholder="Job Type" />
                                            </SelectTrigger>
                                            <SelectContent className="bg-primary-900 border-primary-700">
                                                <SelectItem value="all" className="text-white hover:bg-primary-800">All Types</SelectItem>
                                                <SelectItem value="full-time" className="text-white hover:bg-primary-800">Full-time</SelectItem>
                                                <SelectItem value="part-time" className="text-white hover:bg-primary-800">Part-time</SelectItem>
                                                <SelectItem value="contract" className="text-white hover:bg-primary-800">Contract</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Button 
                                            onClick={handleSearch}
                                            className="h-14 px-8 bg-gradient-to-r from-primary-500 to-violet-500 hover:from-primary-400 hover:to-violet-400 text-white font-semibold rounded-xl shadow-lg shadow-primary-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-primary-500/40"
                                            data-testid="search-button"
                                        >
                                            <Search className="w-5 h-5 mr-2" />
                                            Search
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            {/* Quick Stats */}
                            <div className="flex flex-wrap justify-center gap-6 mt-10">
                                <div className="flex items-center gap-2 text-primary-200">
                                    <Briefcase className="w-4 h-4 text-primary-400" />
                                    <span className="text-sm">500+ Active Jobs</span>
                                </div>
                                <div className="flex items-center gap-2 text-primary-200">
                                    <Building2 className="w-4 h-4 text-primary-400" />
                                    <span className="text-sm">4,500+ Agencies</span>
                                </div>
                                <div className="flex items-center gap-2 text-primary-200">
                                    <Users className="w-4 h-4 text-primary-400" />
                                    <span className="text-sm">10,000+ Job Seekers</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Subscription Banner for Non-Subscribers */}
                {!hasAccess && (
                    <div className="relative bg-gradient-to-r from-primary-800/50 to-violet-800/50 border-y border-primary-700/50 backdrop-blur-sm">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
                                <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/20 border border-amber-500/30">
                                    <Star className="w-4 h-4 text-amber-400" />
                                    <span className="text-xs font-medium text-amber-300">PRO TIP</span>
                                </div>
                                <p className="text-sm text-primary-100">
                                    <span className="font-bold text-white">Create a free account</span> to unlock company names & apply directly
                                </p>
                                <Button 
                                    variant="ghost"
                                    className="text-primary-200 hover:text-white hover:bg-white/10 p-0 h-auto text-sm gap-1"
                                    onClick={() => navigate('/login')}
                                    data-testid="learn-more-btn"
                                >
                                    Sign up free <ArrowRight className="w-3 h-3" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Jobs List */}
                <div className="bg-white min-h-screen">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

                    {loading ? (
                        <div className="flex flex-col justify-center items-center py-20">
                            <div className="relative">
                                <div className="w-16 h-16 rounded-full border-4 border-primary-200 border-t-primary-600 animate-spin" />
                                <Briefcase className="w-6 h-6 text-primary-600 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                            </div>
                            <p className="text-neutral-500 mt-4">Finding the best jobs for you...</p>
                        </div>
                    ) : displayedJobs.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="w-20 h-20 rounded-2xl bg-primary-50 border border-primary-100 flex items-center justify-center mx-auto mb-4">
                                <Briefcase className="w-10 h-10 text-primary-400" />
                            </div>
                            <h3 className="text-xl font-semibold text-neutral-900 mb-2">No jobs found</h3>
                            <p className="text-neutral-500">Try adjusting your search filters or explore all jobs</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
                            {displayedJobs.map((job, index) => (
                                <div 
                                    key={job.job_id} 
                                    className={`group relative ${isDeadlinePassed(job.application_deadline) ? 'opacity-60' : ''}`}
                                    data-testid={`job-card-${job.job_id}`}
                                    style={{ animationDelay: `${index * 0.05}s` }}
                                >
                                    {/* Card Glow Effect */}
                                    <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-500 to-violet-500 rounded-2xl blur opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                                    
                                    <div className="relative bg-white rounded-2xl border border-neutral-200 overflow-hidden transition-all duration-300 group-hover:border-primary-300 group-hover:shadow-xl group-hover:shadow-primary-500/10 group-hover:-translate-y-1">
                                        {/* Top Accent Line */}
                                        <div className="h-1 bg-gradient-to-r from-primary-500 via-violet-500 to-fuchsia-500" />
                                        
                                        <div className="p-6">
                                            {/* Header - Job Title & Badge */}
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex-1 pr-3">
                                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                                        <h3 className="font-semibold text-lg text-neutral-900 line-clamp-2 group-hover:text-primary-700 transition-colors" data-testid="job-title">
                                                            {job.title}
                                                        </h3>
                                                        {job.country && (
                                                            <span
                                                                className="text-xs px-2 py-0.5 bg-neutral-100 text-neutral-700 rounded-full font-medium shrink-0"
                                                                data-testid="job-country-badge"
                                                                title={job.country}
                                                            >
                                                                {job.country === 'Canada' ? '🇨🇦' : '🇬🇧'} {job.country}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {/* Company/Agency Name - BLURRED for non-subscribers */}
                                                    <div className={`flex items-center gap-2 text-sm ${!hasAccess ? 'blur-sm select-none' : 'text-primary-600'}`}>
                                                        <Building2 className="w-4 h-4 text-primary-500" />
                                                        <span className="truncate">{job.agency_name}</span>
                                                    </div>
                                                </div>
                                                {/* Job Type Badge */}
                                                <Badge className={`shrink-0 ${getJobTypeBadgeLight(job.job_type)}`}>
                                                    {job.job_type}
                                                </Badge>
                                            </div>

                                            {/* Details Grid */}
                                            <div className="grid grid-cols-2 gap-3 mb-4">
                                                {/* Location */}
                                                <div className="flex items-center gap-2 text-sm text-neutral-600">
                                                    <div className="w-8 h-8 rounded-lg bg-primary-50 flex items-center justify-center">
                                                        <MapPin className="w-4 h-4 text-primary-500" />
                                                    </div>
                                                    <span className="truncate">{job.location}</span>
                                                </div>
                                                {/* Salary */}
                                                <div className="flex items-center gap-2 text-sm text-neutral-600">
                                                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                                                        <PoundSterling className="w-4 h-4 text-emerald-500" />
                                                    </div>
                                                    <span className="truncate">{formatSalary(job)}</span>
                                                </div>
                                            </div>

                                            {/* Time Info */}
                                            <div className="flex items-center justify-between mb-4 text-sm">
                                                {/* Posted Time */}
                                                {job.created_at && (
                                                    <div className="flex items-center gap-2 text-emerald-600">
                                                        <Clock className="w-4 h-4" />
                                                        <span className="font-medium">{formatTimeAgo(job.created_at)}</span>
                                                    </div>
                                                )}
                                                {/* Deadline */}
                                                {job.application_deadline && (
                                                    <div className={`flex items-center gap-1.5 ${isDeadlinePassed(job.application_deadline) ? 'text-red-500' : 'text-neutral-500'}`}>
                                                        <Calendar className="w-4 h-4" />
                                                        <span>{isDeadlinePassed(job.application_deadline) ? 'Closed' : `${new Date(job.application_deadline).toLocaleDateString()}`}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Description */}
                                            <p className="text-sm text-neutral-500 line-clamp-2 mb-5">
                                                {job.description && !job.description.includes('imported from external') 
                                                    ? job.description 
                                                    : 'View full job details by clicking below.'}
                                            </p>

                                            {/* Actions */}
                                            <div className="flex gap-2" onClickCapture={() => recordView(`job:${job.job_id}`)}>
                                                {hasAccess ? (
                                                    // Subscribed user - show apply buttons
                                                    <>
                                                        {!isDeadlinePassed(job.application_deadline) && (
                                                            <>
                                                                {job.apply_url ? (
                                                                    <Button 
                                                                        className="flex-1 bg-gradient-to-r from-primary-600 to-violet-600 hover:from-primary-500 hover:to-violet-500 text-white border-0 shadow-lg shadow-primary-500/20"
                                                                        asChild
                                                                        data-testid="apply-now-btn"
                                                                    >
                                                                        <a href={job.apply_url} target="_blank" rel="noopener noreferrer">
                                                                            <ExternalLink className="w-4 h-4 mr-2" />
                                                                            Apply Now
                                                                        </a>
                                                                    </Button>
                                                                ) : job.apply_email ? (
                                                                    <Button 
                                                                        className="flex-1 bg-gradient-to-r from-primary-600 to-violet-600 hover:from-primary-500 hover:to-violet-500 text-white border-0 shadow-lg shadow-primary-500/20"
                                                                        asChild
                                                                        data-testid="apply-email-btn"
                                                                    >
                                                                        <a href={`mailto:${job.apply_email}?subject=Application for ${job.title}`}>
                                                                            <Mail className="w-4 h-4 mr-2" />
                                                                            Apply via Email
                                                                        </a>
                                                                    </Button>
                                                                ) : (
                                                                    <Button 
                                                                        className="flex-1 bg-gradient-to-r from-primary-600 to-violet-600 hover:from-primary-500 hover:to-violet-500 text-white border-0 shadow-lg shadow-primary-500/20"
                                                                        asChild
                                                                        data-testid="view-agency-btn"
                                                                    >
                                                                        <Link to={`/agencies/${job.agency_id}`}>
                                                                            <Building2 className="w-4 h-4 mr-2" />
                                                                            View Agency
                                                                        </Link>
                                                                    </Button>
                                                                )}
                                                            </>
                                                        )}
                                                        {isDeadlinePassed(job.application_deadline) && (
                                                            <Button 
                                                                variant="outline" 
                                                                className="flex-1 border-neutral-300 text-neutral-400" 
                                                                disabled
                                                            >
                                                                Application Closed
                                                            </Button>
                                                        )}
                                                    </>
                                                ) : (
                                                    // Non-subscribed user - show Apply Now button
                                                    <Button 
                                                        className="flex-1 bg-gradient-to-r from-primary-600 to-violet-600 hover:from-primary-500 hover:to-violet-500 text-white border-0 shadow-lg shadow-primary-500/20 transition-all duration-300"
                                                        onClick={() => handleApplyClick(job)}
                                                        data-testid="apply-now-subscribe-btn"
                                                    >
                                                        <Sparkles className="w-4 h-4 mr-2" />
                                                        Apply Now
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-4 mt-12">
                            <Button
                                variant="outline"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="border-primary-300 text-primary-700 hover:bg-primary-50 hover:text-primary-800 disabled:opacity-50"
                                data-testid="prev-page-btn"
                            >
                                Previous
                            </Button>
                            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary-50 border border-primary-200">
                                <span className="text-neutral-500">Page</span>
                                <span className="text-primary-700 font-bold">{page}</span>
                                <span className="text-neutral-500">of {totalPages}</span>
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                                className="border-primary-300 text-primary-700 hover:bg-primary-50 hover:text-primary-800 disabled:opacity-50"
                                data-testid="next-page-btn"
                            >
                                Next
                            </Button>
                        </div>
                    )}
                    </div>
                </div>
            </div>

            {/* Subscription Modal - Premium Purple Theme */}
            {showSubscriptionModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    {/* Backdrop */}
                    <div 
                        className="absolute inset-0 bg-primary-950/80 backdrop-blur-md"
                        onClick={() => setShowSubscriptionModal(false)}
                    />
                    
                    {/* Modal Content */}
                    <div className="relative max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-200">
                        {/* Card Glow */}
                        <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 via-violet-500 to-fuchsia-500 rounded-3xl blur opacity-50" />
                        
                        <div className="relative bg-primary-900/95 backdrop-blur-xl rounded-2xl border border-primary-700/50 overflow-hidden">
                            {/* Close Button */}
                            <button 
                                onClick={() => setShowSubscriptionModal(false)}
                                className="absolute top-4 right-4 p-2 rounded-full bg-primary-800/50 hover:bg-primary-700/50 transition-colors z-10"
                                data-testid="close-modal-btn"
                            >
                                <X className="w-5 h-5 text-primary-300" />
                            </button>

                            {/* Header with gradient */}
                            <div className="relative px-6 py-8 overflow-hidden">
                                {/* Background Effects */}
                                <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-violet-600 to-primary-700" />
                                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-fuchsia-500/20 rounded-full blur-2xl" />
                                
                                <div className="relative">
                                    <div className="flex items-center gap-2 mb-3">
                                        <div className="px-3 py-1 rounded-full bg-white/20 backdrop-blur-sm border border-white/20">
                                            <span className="text-xs font-medium text-white flex items-center gap-1.5">
                                                <Sparkles className="w-3 h-3" />
                                                Almost there!
                                            </span>
                                        </div>
                                    </div>
                                    <h3 className="text-2xl font-bold mb-2 text-white">
                                        Unlock This Job Application
                                    </h3>
                                    {selectedJob && (
                                        <p className="text-primary-100 flex items-center gap-2">
                                            <Briefcase className="w-4 h-4" />
                                            {selectedJob.title} • {selectedJob.location}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Body */}
                            <div className="p-6">
                                <p className="text-primary-200 mb-6">
                                    Create a free account to see full job details, company names, and apply directly to this position and thousands more.
                                </p>

                                {/* Benefits */}
                                <div className="space-y-3 mb-6">
                                    {[
                                        'Apply directly to all job listings',
                                        'See company names & full details',
                                        'Contact recruiters directly',
                                        'Save & shortlist your favorites'
                                    ].map((benefit, i) => (
                                        <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-primary-800/30">
                                            <div className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center">
                                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                            </div>
                                            <span className="text-primary-100">{benefit}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Pricing highlight */}
                                <div className="relative mb-6 overflow-hidden">
                                    <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-xl" />
                                    <div className="relative p-4 rounded-xl border border-amber-500/30">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-sm text-amber-300 font-medium mb-1">Registered Users</p>
                                                <p className="text-3xl font-bold text-white">Free<span className="text-base font-normal text-primary-300"> · forever</span></p>
                                            </div>
                                            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30">
                                                <Zap className="w-4 h-4 text-amber-400" />
                                                <span className="text-sm font-medium text-amber-300">Best Value</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* CTA Buttons */}
                                <div className="space-y-3">
                                    <Button 
                                        className="w-full h-12 bg-gradient-to-r from-primary-500 to-violet-500 hover:from-primary-400 hover:to-violet-400 text-white text-lg font-semibold shadow-lg shadow-primary-500/30 border-0"
                                        onClick={() => navigate('/login')}
                                        data-testid="view-plans-btn"
                                    >
                                        <Crown className="w-5 h-5 mr-2" />
                                        Sign Up Free
                                    </Button>
                                    {!isAuthenticated && (
                                        <Button 
                                            variant="outline"
                                            className="w-full h-12 border-primary-600 text-primary-200 hover:bg-primary-800/50 hover:text-white"
                                            onClick={() => {
                                                setShowSubscriptionModal(false);
                                                navigate('/login');
                                            }}
                                            data-testid="sign-in-btn"
                                        >
                                            Already have an account? Sign In
                                        </Button>
                                    )}
                                </div>

                                <p className="text-center text-xs text-primary-400 mt-4 flex items-center justify-center gap-2">
                                    <CheckCircle2 className="w-3 h-3" />
                                    No card needed • Takes 30 seconds
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Layout>
    );
};

export default JobsPage;
